import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Field, Input, Textarea, Select, Badge, Spinner } from "@/components/ui";
import { AssignmentEditor } from "@/components/assignment-editors";
import { useAssignment, useUpdateAssignment } from "@/lib/queries";

export const Route = createFileRoute("/app/assignments_/$id")({
  component: EditAssignmentPage,
});

function EditAssignmentPage() {
  const { id } = Route.useParams();
  const { data: a, isLoading, isError } = useAssignment(id);
  const update = useUpdateAssignment();

  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [languageCode, setLanguageCode] = useState("en");
  const [tags, setTags] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (a) {
      setTitle(a.title);
      setInstructions(a.instructions);
      setLanguageCode(a.languageCode);
      setTags(a.tags);
    }
  }, [a]);

  if (isLoading) return <Spinner label={t("app.loading")} />;
  if (isError || !a) return <p className="text-destructive">{t("repo.notFound")}</p>;

  const canEdit = a.canEdit !== false;
  async function save(patch?: { status?: "draft" | "ready" | "archived" }) {
    await update.mutateAsync({
      id,
      patch: {
        title: title.trim() || a!.title,
        instructions,
        languageCode,
        tags,
        ...(patch ?? {}),
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{a.title}</h1>
          <Badge tone="blue">{a.typeName}</Badge>
          <Badge
            tone={a.status === "ready" ? "green" : a.status === "archived" ? "red" : "amber"}
          >
            {t(`assignments.status.${a.status}`)}
          </Badge>
          {!canEdit && <Badge tone="purple">{t("edit.readOnly")}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <a href={`/s/${a.id}`} target="_blank" rel="noreferrer">
            <Button variant="ghost">🔗 {t("edit.studentView")}</Button>
          </a>
          <Link to="/app/assign/$id" params={{ id: a.id }}>
            <Button>{t("common.assign")}</Button>
          </Link>
          <Link to="/app/assignments">
            <Button variant="ghost">← {t("common.back")}</Button>
          </Link>
        </div>
      </div>

      <Card className="max-w-2xl space-y-4 p-6">
        <h2 className="text-sm font-semibold">{t("edit.meta")}</h2>
        <Field label={t("new.titleLabel")}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} readOnly={!canEdit} />
        </Field>
        <Field label={t("new.instructionsLabel")}>
          <Textarea
            rows={4}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            readOnly={!canEdit}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("new.languageLabel")}>
            <Select
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              disabled={!canEdit}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </Select>
          </Field>
          <Field label={t("new.tagsLabel")}>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} readOnly={!canEdit} />
          </Field>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled={update.isPending} onClick={() => void save()}>
              {update.isPending ? t("common.saving") : t("common.save")}
            </Button>
            <Button variant="outline" disabled={update.isPending} onClick={() => void save({ status: "ready" })}>
              {t("edit.markReady")}
            </Button>
            {a.status !== "archived" ? (
              <Button variant="ghost" onClick={() => void save({ status: "archived" })}>
                🗄 {t("common.archive")}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => void save({ status: "draft" })}>
                ↩ {t("common.restore")}
              </Button>
            )}
            {saved && <span className="text-sm text-green-600">✓ {t("edit.saved")}</span>}
          </div>
        )}
      </Card>

      <Card className="space-y-4 p-6">
        <h2 className="text-sm font-semibold">{t("edit.content")}</h2>
        <AssignmentEditor detail={a} readOnly={!canEdit} />
      </Card>
    </div>
  );
}